import React, {useState, useTransition} from 'react';
import {Button, Form, Input, Popconfirm, Avatar, Tag, message, Modal, Tooltip} from 'antd';
import type {TableColumnsType} from 'antd';
import type {SearchProps} from 'antd/es/input/Search';

import {
  PlusOutlined,
  UnlockOutlined,
  LockOutlined,
  EditFilled,
  DollarOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import Api from 'configs';

import {useGetShipers} from 'hooks/shiper/useGetShipers';
import {useDeleteShipers, useUnblockShipperEarly} from 'hooks/shiper/useUpdateShiper';
import {useToggleUser, useVerifyUser} from 'hooks/users/useUpdateUsers';
import {PaginationCustom} from 'components/element/PaginationCustom';
import {TableCustom} from 'components/element/TableCustom';
import {LoadedMoney} from 'components/element/LoadedMoney';
import {
  addKeyData,
  formatNumberNegativeToString,
  formatNumberValue,
  formatDate,
} from 'utils';
import {FormShip} from 'components/ship/FormShip';

export const Shiper: React.FC = () => {
  const [isPending, startTransition] = useTransition();

  const [form] = Form.useForm();
  const [formToMoney] = Form.useForm();
  const [filters, setFilters] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [isShowPopup, setIsShowPopup] = useState(false);
  const [isId, setIsId] = useState('');
  const [blockingDriver, setBlockingDriver] = useState<{userId: string; name: string} | null>(null);
  const [blockReasonInput, setBlockReasonInput] = useState('');
  const [isSubmittingBlock, setIsSubmittingBlock] = useState(false);
  const {data: getData, isLoading, refetch} = useGetShipers(filters);
  const {mutateAsync: onDeleteShipers,isLoading: isLoadingDelete} = useDeleteShipers()
  const {mutateAsync: onUnblockEarly} = useUnblockShipperEarly();
  const {mutateAsync: onToggleUser} = useToggleUser();
  const {mutateAsync: onVerifyUser} = useVerifyUser();
  const handleconfirm = async (id: string) => {
    await onToggleUser(id);
    await refetch();
  };
  const handleConfirmBlock = async () => {
    if (!blockingDriver) return;
    try {
      setIsSubmittingBlock(true);
      await onToggleUser({
        idUser: blockingDriver.userId,
        reason: blockReasonInput.trim() || undefined,
      });
      message.success(`Đã khóa tài khoản tài xế ${blockingDriver.name}`);
      setBlockingDriver(null);
      setBlockReasonInput('');
      await refetch();
    } catch {
      message.error('Lỗi khi khóa tài xế');
    } finally {
      setIsSubmittingBlock(false);
    }
  };
  const handleUnblockEarly = async (shipId: string, name?: string) => {
    try {
      await onUnblockEarly(shipId);
      message.success(`Đã hỗ trợ mở khóa sớm cho tài xế ${name || ''}!`);
      await refetch();
    } catch {
      message.error('Lỗi khi mở khóa sớm cho tài xế');
    }
  };
  const [loadingExport, setLoadingExport] = useState(false);
  const handleExportDriverExcel = async () => {
    try {
      setLoadingExport(true);
      const response = await Api.get('/reports/export/drivers', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'danh-sach-tai-xe.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Xuất file Excel tài xế thành công');
    } catch {
      message.error('Lỗi khi xuất file Excel');
    } finally {
      setLoadingExport(false);
    }
  };
  const columns: TableColumnsType = [
    {
      title: 'Tên Shiper',
      dataIndex: 'name',
      key: 'name',
      align: 'center',
      fixed: 'left',
      width: 160,
    },
    {
      title: 'Avatar',
      dataIndex: 'avatar',
      key: 'avatar',
      align: 'center',
      width: 80,
      render: data => {
        return <Avatar src={data} />;
      },
    },
    {
      title: 'Số điện thoại',
      dataIndex: 'user',
      key: 'numberPhone',
      align: 'center',
      width: 140,
      render: data => {
        return <p className="whitespace-nowrap">{data?.numberPhone}</p>;
      },
    },
    {
      title: 'Địa chỉ',
      dataIndex: 'address',
      key: 'address',
      align: 'center',
      width: 220,
    },
    {
      title: 'Tiền trong ví',
      dataIndex: 'balance',
      key: 'balance',
      align: 'center',
      width: 150,
      sorter: true,
      render: data => {
        return (
          <div className="whitespace-nowrap font-medium">
            <p>{formatNumberValue(data)}</p>
          </div>
        );
      },
    },
    {
      title: 'Biển số xe',
      dataIndex: 'vehicleBKS',
      key: 'vehicleBKS',
      align: 'center',
      width: 130,
      render: data => <span className="whitespace-nowrap">{data}</span>,
    },
    {
      title: 'Tên xe',
      dataIndex: 'vehicleName',
      key: 'vehicleName',
      align: 'center',
      width: 130,
      render: data => <span className="whitespace-nowrap">{data}</span>,
    },
    {
      title: 'Trạng thái',
      key: 'isBlocked',
      align: 'center',
      width: 170,
      render: (_: any, record: any) => {
        const user = record?.user;
        const isBlocked = user?.isBlocked;
        const isIgnoreBlocked =
          isBlocked &&
          (record?.typeBlock === 'IGNORE_LIMIT' ||
            record?.blockReason === 'IGNORE_LIMIT');

        return (
          <div className="flex flex-col items-center gap-1">
            {isIgnoreBlocked ? (
              <Tag color="warning" className="m-0 font-medium">
                Tạm khóa (Bỏ qua đơn)
              </Tag>
            ) : isBlocked ? (
              <Tag color="error" className="m-0 font-medium">
                Đã khóa (Admin)
              </Tag>
            ) : (
              <Tag color="success" className="m-0 font-medium">
                Bình thường
              </Tag>
            )}

            {isIgnoreBlocked && record?.unblockAt && (
              <span className="text-[11px] text-gray-500">
                Mở tự động: {formatDate(record.unblockAt)}
              </span>
            )}

            {!isIgnoreBlocked && isBlocked && record?.blockReason && (
              <Tooltip
                title={record.blockReason}
                placement="topLeft"
                overlayInnerStyle={{maxWidth: 350, wordBreak: 'break-word'}}>
                <span className="text-[11px] text-red-500 font-normal max-w-[150px] truncate cursor-pointer block">
                  Lý do: {record.blockReason}
                </span>
              </Tooltip>
            )}

            {isIgnoreBlocked ? (
              <Popconfirm
                title="Hỗ trợ mở khóa sớm"
                description={`Bạn có chắc muốn hỗ trợ MỞ KHÓA SỚM cho tài xế ${record.name || ''}?`}
                okText="Mở khóa ngay"
                cancelText="Hủy"
                onConfirm={() => handleUnblockEarly(record?._id, record?.name)}>
                <Button
                  size="small"
                  type="primary"
                  icon={<UnlockOutlined />}
                  className="!bg-[#faad14] hover:!bg-[#d48806] !text-white text-xs mt-1 shadow-sm">
                  Mở khóa sớm
                </Button>
              </Popconfirm>
            ) : isBlocked ? (
              <Popconfirm
                title="Mở khóa tài xế"
                description={`Bạn có chắc muốn mở khóa cho tài xế ${record.name || ''}?`}
                okText="Đồng ý"
                cancelText="Hủy"
                onConfirm={() => handleconfirm(user?._id)}>
                <Button
                  size="small"
                  type="primary"
                  icon={<UnlockOutlined />}
                  className="text-xs mt-1">
                  Mở khóa
                </Button>
              </Popconfirm>
            ) : (
              <Button
                size="small"
                danger
                icon={<LockOutlined />}
                className="text-xs mt-1"
                onClick={() => {
                  setBlockingDriver({
                    userId: user?._id,
                    name: record?.name || 'Tài xế',
                  });
                  setBlockReasonInput('');
                }}>
                Khóa
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: 'Xác minh',
      dataIndex: 'user',
      key: 'verified',
      align: 'center',
      width: 130,
      render: (data: any) => {
        return (
          <Popconfirm
            title={`Kích hoạt người dùng`}
            description={`Bạn có chắc muốn kích hoạt người dùng`}
            okText="Yes"
            disabled={data.verified}
            cancelText="No"
            onConfirm={async () => {
              await onVerifyUser(data?._id);
              await refetch();
            }}>
            <div>
              {data.verified ? (
                <Tag color="success">Đã xác minh</Tag>
              ) : (
                <Tag className="verify" color="error">
                  Chưa xác minh
                </Tag>
              )}
            </div>
          </Popconfirm>
        );
      },
    },
    {
      title: 'Tình trạng',
      dataIndex: 'isActive',
      align: 'center',
      key: 'isActive',
      width: 130,
      render: (data: any) => {
        return (
          <div>
            {data ? (
              <Tag color="success">Đang hoạt động</Tag>
            ) : (
              <Tag color="error">Ngừng hoạt động</Tag>
            )}
          </div>
        );
      },
    },
    // Q: Cột sao đánh giá tài xế
    {
      title: 'Đánh giá',
      dataIndex: 'average',
      key: 'average',
      align: 'center',
      width: 110,
      render: (data: number) => <span className="whitespace-nowrap">⭐ {data ? Number(data).toFixed(1) : '5.0'}</span>,
    },
    // J: Tỉ lệ hủy đơn
    {
      title: 'Hủy ngày/tháng',
      key: 'cancelCount',
      align: 'center',
      width: 140,
      render: (_: any, record: any) => {
        const daily = record?.dailyCancelCount || 0;
        const monthly = record?.monthlyCancelCount || 0;
        return (
          <span className={`whitespace-nowrap ${daily > 2 ? 'text-red-500 font-semibold' : ''}`}>
            {daily} / {monthly} lần
          </span>
        );
      },
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      width: 160,
      render: data => {
        return (
          <div className="whitespace-nowrap">
            <p>{formatDate(data)}</p>
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: '',
      key: 'action',
      align: 'center',
      width: 300,
      render: (text, record: any) => {
        return (
          <div className="flex justify-center gap-2 px-0">
            <Button
              className="!border-white	 bg-[#2bec82] hover:!bg-[#2bec82] !text-white !font-medium"
              onClick={() => {
                formToMoney.resetFields();
                const data = {
                  ...record,
                  balance: formatNumberNegativeToString(record.balance || 0),
                };
                formToMoney.setFieldsValue(data);
                setIsOpen(true);
              }}
              icon={<DollarOutlined />}>
              Nạp tiền
            </Button>

            <Button
              onClick={() => {
                const data = {
                  ...record,
                  numberPhone: record.user.numberPhone,
                };
                setIsId(data?._id);
                form.setFieldsValue({
                  ...data,
                  'infoBankAccount.name': data.infoBankAccount.name,
                  'infoBankAccount.number': data.infoBankAccount.number,
                  'infoBankAccount.qr': data.infoBankAccount.qr,
                });
                setIsShowPopup(true);
              }}
              icon={<EditFilled />}
              className="!border-white	 bg-[#eca52b] hover:!bg-[#edb555] !text-white !font-medium">
              Chỉnh sửa
            </Button>
            <Popconfirm
              title="Xoá tài xế"
              description="Bạn có chắc muốn xoá tài xế này không ?"
              onConfirm={ async()=>{
                    await onDeleteShipers(record?._id);
                    await refetch();
    
              }}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No">
              <Button loading={isLoadingDelete} className="!border-white bg-[#ed250e] hover:!bg-[#f25c4b] !text-white !font-medium">
                Xoá
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  const onSeachValue: SearchProps['onSearch'] = (value, _e, info) => {
    setFilters({...filters, search: value});
  };
  const handleTableChange = (filter: any, sorter: any) => {
    if (sorter) {
      setFilters({...filters, sorter: sorter});
    } else {
      setFilters({...filters, sorter: null});
    }
  };
  return (
    <div className="container mx-auto">
      <div className="flex-col gap-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Tài xế</h2>
          <div className="flex gap-2">
            <div>
              <Input.Search
                className="w-96"
                placeholder="Search theo tên hoặc số điện thoại"
                onSearch={onSeachValue}
                enterButton
              />
            </div>
            <Button
              className="!bg-green-600 hover:!bg-green-500 !text-white font-bold"
              icon={<DownloadOutlined />}
              loading={loadingExport}
              onClick={handleExportDriverExcel}>
              Xuất Excel Tài xế
            </Button>
            <Button
              className="font-bold"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                startTransition(() => {
                  setIsId('');

                  setIsShowPopup(true);
                });
              }}>
              Thêm tài xế
            </Button>
          </div>
        </div>
        <div className=" flex flex-col items-end gap-3">
          <TableCustom
            isLoading={isLoading}
            columns={columns}
            data={addKeyData(getData?.result)}
            onChange={handleTableChange}
            scroll={{x: 2150, y: 590}}
          />
          <PaginationCustom
            totalPage={getData?.totalPage ?? 1}
            current={getData?.page ?? 1}
            pageSize={getData?.pageSize ?? 10}
            onChange={(page, pageSize) => {
              setFilters({...filters, page, pageSize});
            }}
            onShowSizeChange={(current, size) => {
              setFilters({...filters, page: current, pageSize: size});
            }}
          />
          {isOpen ? (
            <LoadedMoney
              form={formToMoney}
              setIsOpen={setIsOpen}
              isOpen={isOpen}
              refetch={refetch}
            />
          ) : null}
        </div>
      </div>
      <FormShip
        form={form}
        isShowPopup={isShowPopup}
        isId={isId}
        setIsShowPopup={setIsShowPopup}
        setIsId={setIsId}
        refetch={refetch}
      />
      <Modal
        title={`Khóa tài khoản tài xế: ${blockingDriver?.name || ''}`}
        open={Boolean(blockingDriver)}
        onCancel={() => setBlockingDriver(null)}
        onOk={handleConfirmBlock}
        confirmLoading={isSubmittingBlock}
        okText="Xác nhận khóa"
        cancelText="Hủy"
        okButtonProps={{danger: true}}>
        <div className="py-2">
          <p className="text-sm text-gray-600 mb-2">
            Nhập lý do khóa tài xế (mô tả này sẽ được lưu lại và hiển thị cho tài xế):
          </p>
          <Input.TextArea
            rows={3}
            placeholder="Ví dụ: Vi phạm quy chế giao hàng, nhiều khiếu nại thái độ, ..."
            value={blockReasonInput}
            onChange={e => setBlockReasonInput(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};
